import { createContext, useCallback, useContext, useMemo, useState } from "react";

// Estado compartilhado leve, em Context. Centraliza:
//
//   - jobId atualmente "rastreado" (para tabs de status/cancel/retry).
//   - último payload usado (para reusar entre Validate e CreateJob).
//   - ponto pendente para o mapa vetorial (hand-off da aba Geocoding).
//
// Mantemos o store simples — preferimos prop drilling para o resto.

// Ponto que uma aba pede ao mapa vetorial para posicionar quando ele abrir
// (marker + flyTo + popup). É consumido e zerado pelo TabMapVector no mount —
// mesma ideia do lastPayload, que viaja entre Validate e CreateJob.
export type PendingMapPoint = {
  lat: number;
  lon: number;
  label: string;
};

export type AppState = {
  trackedJobId: string | null;
  setTrackedJobId: (id: string | null) => void;
  lastPayload: string | null;
  setLastPayload: (json: string | null) => void;
  lastJobIds: string[];
  pushJobId: (id: string) => void;
  pendingMapPoint: PendingMapPoint | null;
  setPendingMapPoint: (point: PendingMapPoint | null) => void;
};

const ctx = createContext<AppState | null>(null);

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [trackedJobId, setTrackedJobId] = useState<string | null>(null);
  const [lastPayload, setLastPayload] = useState<string | null>(null);
  const [lastJobIds, setLastJobIds] = useState<string[]>([]);
  const [pendingMapPoint, setPendingMapPoint] = useState<PendingMapPoint | null>(null);

  const pushJobId = useCallback((id: string) => {
    setLastJobIds((prev) => {
      const filtered = prev.filter((x) => x !== id);
      return [id, ...filtered].slice(0, 8);
    });
  }, []);

  const value = useMemo<AppState>(
    () => ({
      trackedJobId,
      setTrackedJobId,
      lastPayload,
      setLastPayload,
      lastJobIds,
      pushJobId,
      pendingMapPoint,
      setPendingMapPoint,
    }),
    [trackedJobId, lastPayload, lastJobIds, pushJobId, pendingMapPoint],
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
}

export function useAppState(): AppState {
  const v = useContext(ctx);
  if (!v) throw new Error("useAppState fora do AppStateProvider");
  return v;
}
